/**
 * Thin REST client for the Google APIs, ported from garage-buddy-web.
 * Attaches the bearer token, retries once on 401 (expired mid-flight) and
 * backs off on the rate-limit / transient 5xx codes Sheets is fond of.
 */
import { auth, AuthError } from './auth'

export class GoogleAPIError extends Error {
  constructor(readonly status: number, readonly detail: string, url: string) {
    super(`Google API ${status}: ${detail} (${new URL(url).pathname})`)
    this.name = 'GoogleAPIError'
  }

  /** The token was rejected outright — the user must re-consent. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403
  }
}

const MAX_ATTEMPTS = 4
/**
 * Sheets counts requests per minute, so a refused one waits out the minute:
 * 1 + 2 + 4 + 8 + 16 + 30 seconds before giving up.
 */
const RATE_LIMIT_ATTEMPTS = 6

export function retryDelay(attempt: number, rateLimited = false): number {
  if (rateLimited) return Math.min(30_000, 1000 * 2 ** attempt) + Math.random() * 500
  return Math.min(8000, 500 * 2 ** attempt) + Math.random() * 250
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Raw body (already-encoded bytes), used for multipart Drive uploads. */
  rawBody?: BodyInit
  contentType?: string
  /** Return the response as bytes rather than JSON. */
  blob?: boolean
}

async function send(url: string, options: RequestOptions, attempt = 0): Promise<Response> {
  const token = await auth.accessToken()
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  let body: BodyInit | undefined
  if (options.rawBody !== undefined) {
    body = options.rawBody
    if (options.contentType) headers['Content-Type'] = options.contentType
  } else if (options.body !== undefined) {
    body = JSON.stringify(options.body)
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(url, { method: options.method ?? 'GET', headers, body })
  if (response.ok) return response

  // A token can expire between the check and the call; renew once and retry.
  if (response.status === 401 && attempt === 0) {
    auth.invalidate()
    return send(url, options, attempt + 1)
  }
  // 429 means the request was refused, so any request can be sent again. A 5xx
  // may come after the server applied it: a POST (an append, a delete by row
  // number, a new file) is not repeated then, or it would apply twice.
  const method = options.method ?? 'GET'
  const limited = response.status === 429
  if ((limited ? attempt < RATE_LIMIT_ATTEMPTS : response.status >= 500 && method !== 'POST' && attempt < MAX_ATTEMPTS)) {
    await new Promise((r) => setTimeout(r, retryDelay(attempt, limited)))
    return send(url, options, attempt + 1)
  }
  throw new GoogleAPIError(response.status, (await response.text()).slice(0, 400), url)
}

export const api = {
  async get<T>(url: string): Promise<T> {
    return (await send(url, {})).json() as Promise<T>
  },
  async post<T>(url: string, body: unknown): Promise<T> {
    const response = await send(url, { method: 'POST', body })
    return parseMaybeEmpty<T>(response)
  },
  async put<T>(url: string, body: unknown): Promise<T> {
    const response = await send(url, { method: 'PUT', body })
    return parseMaybeEmpty<T>(response)
  },
  async patch<T>(url: string, body: unknown): Promise<T> {
    const response = await send(url, { method: 'PATCH', body })
    return parseMaybeEmpty<T>(response)
  },
  async delete(url: string): Promise<void> {
    await send(url, { method: 'DELETE' })
  },
  async upload<T>(url: string, contentType: string, rawBody: BodyInit): Promise<T> {
    const response = await send(url, { method: 'POST', contentType, rawBody })
    return parseMaybeEmpty<T>(response)
  },
  /** Starts a resumable upload and returns the session URL the bytes are PUT to. */
  async startUpload(url: string, metadata: unknown): Promise<string> {
    const response = await send(url, { method: 'POST', body: metadata })
    const location = response.headers.get('Location')
    if (!location) throw new Error('Drive did not return an upload address')
    return location
  },
  /** An upload with a method other than POST (PATCH replaces a file's content). */
  async uploadWith<T>(method: string, url: string, contentType: string, rawBody: BodyInit): Promise<T> {
    const response = await send(url, { method, contentType, rawBody })
    return parseMaybeEmpty<T>(response)
  },
  async download(url: string): Promise<Blob> {
    return (await send(url, {})).blob()
  },
}

/** Some endpoints (DELETE-ish PATCHes) answer 204 with no body. */
async function parseMaybeEmpty<T>(response: Response): Promise<T> {
  const text = await response.text()
  return (text ? JSON.parse(text) : {}) as T
}

export { AuthError }
